import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  abortableFetch,
  defaultGetItem,
  DomainObject,
  getErrorMessage,
  ListSource,
  SnackbarContext,
  SnackbarVariant,
  useEventCallback,
} from '@eas/common-web';

export function useTicketListSource<TYPE extends DomainObject>({
  url,
  apiUrl,
  skip = false,
}: {
  url: string;
  apiUrl?: string;
  skip?: boolean;
}): ListSource<TYPE> {
  const intl = useIntl();
  const { showSnackbar } = useContext(SnackbarContext);
  const [counter, setCounter] = useState(0);
  const requestKey = JSON.stringify({ url, skip, counter });
  const currentRequestKey = useRef(requestKey);
  const renderedRequestKey = useRef(requestKey);
  const currentGeneration = useRef(0);

  if (renderedRequestKey.current !== requestKey) {
    renderedRequestKey.current = requestKey;
    currentGeneration.current += 1;
  }

  currentRequestKey.current = requestKey;

  const [state, setState] = useState<{
    key: string;
    generation: number;
    items: TYPE[];
    loading: boolean;
  }>({
    key: requestKey,
    generation: currentGeneration.current,
    items: [],
    loading: !skip,
  });

  useEffect(() => {
    const key = requestKey;
    const generation = currentGeneration.current;
    let request: ReturnType<typeof abortableFetch> | undefined;

    async function load() {
      if (skip) {
        setState({ key, generation, items: [], loading: false });
        return;
      }

      setState({ key, generation, items: [], loading: true });

      try {
        request = abortableFetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        const items = (await request.json()) as TYPE[];

        if (
          currentRequestKey.current === key &&
          currentGeneration.current === generation
        ) {
          setState({ key, generation, items, loading: false });
        }
      } catch (err) {
        if (
          err.name !== 'AbortError' &&
          currentRequestKey.current === key &&
          currentGeneration.current === generation
        ) {
          setState({ key, generation, items: [], loading: false });

          const message = intl.formatMessage(
            {
              id: 'EAS_FETCH_MSG_ERROR',
              defaultMessage: 'Chyba načtení dat: {detail}',
            },
            { detail: getErrorMessage(err) }
          );
          showSnackbar(message, SnackbarVariant.ERROR);
        }
      }
    }

    load();

    return () => request?.abort();
    // Request dependencies are captured in requestKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey]);

  const reset = useEventCallback(() => {
    setCounter((value) => value + 1);
  });

  const detailRequest = useRef(0);
  const loadDetail = useEventCallback(async (item: TYPE) => {
    if (apiUrl == null) {
      return item;
    }

    const id = ++detailRequest.current;
    const key = currentRequestKey.current;
    const generation = currentGeneration.current;
    const detail = (await defaultGetItem(apiUrl, item.id).json()) as TYPE;

    if (
      id !== detailRequest.current ||
      key !== currentRequestKey.current ||
      generation !== currentGeneration.current
    ) {
      return new Promise<TYPE>(() => undefined);
    }

    return detail;
  });

  const currentState =
    state.key === requestKey &&
    state.generation === currentGeneration.current
      ? state
      : {
          key: requestKey,
          generation: currentGeneration.current,
          items: [],
          loading: !skip,
        };

  return useMemo(
    () => ({
      items: currentState.items,
      loading: currentState.loading,
      reset,
      loadDetail,
    }),
    [currentState.items, currentState.loading, reset, loadDetail]
  );
}
